import { Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { QueuedMessage } from "../chat-provider";

interface QueueDisplayProps {
  queuedMessages: QueuedMessage[];
  rateLimitedUntil?: number;
  onRemove?: (id: string) => void;
}

export function QueueDisplay({
  queuedMessages,
  rateLimitedUntil,
  onRemove,
}: QueueDisplayProps) {
  if (queuedMessages.length === 0) return null;

  const isRateLimited = rateLimitedUntil && rateLimitedUntil > Date.now();

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-2">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-medium text-amber-600 dark:text-amber-400">
          <Clock className="h-3.5 w-3.5" />
          {isRateLimited
            ? `Rate limited — messages queued (${queuedMessages.length})`
            : `Waiting for current response — ${queuedMessages.length} message${queuedMessages.length > 1 ? "s" : ""} queued`}
        </div>
        <div className="space-y-1.5">
          {queuedMessages.map((msg, i) => (
            <div
              key={msg.id}
              className="flex items-start gap-2 rounded-md bg-background/60 px-2.5 py-1.5 text-sm"
            >
              <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                #{i + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-foreground/80">
                {msg.text.length > 120 ? msg.text.slice(0, 120) + "…" : msg.text}
              </span>
              {onRemove && (
                <Button
                  size="icon-xs"
                  variant="ghost"
                  onClick={() => onRemove(msg.id)}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={`Remove queued message #${i + 1}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
